import {Injectable} from '@angular/core';

import {BehaviorSubject, Subject} from 'rxjs';

import {SelectionDimension} from '@core/services/api/api-edition-configuration.service';

/** Typy wymiaru wg konwencji backendu (App\Enums\InventoryWork\ArtworkDimension). */
const WHOLE_DIMENSION = 1;
const FRAME_DIMENSION = 2;
const DIAMETER_DIMENSION = 3;

/** Znak mnożenia (U+00D7) — ten sam, który backend renderuje jako `&times;`. */
const SEPARATOR = ' × ';

/**
 * Osie niosące wartość dla danego typu wymiaru. Średnica ma tylko X, rama X i Y,
 * a wymiar całościowy X, Y i Z — dokładnie jak `get_dimension_as_string` na backendzie.
 */
function axesForType(dimension: SelectionDimension): number[] {
  switch (dimension.type) {
    case WHOLE_DIMENSION:
      return [dimension.x, dimension.y, dimension.z];
    case FRAME_DIMENSION:
      return [dimension.x, dimension.y];
    case DIAMETER_DIMENSION:
      return [dimension.x];
    default:
      return [];
  }
}

/** Pojedynczy wymiar jako string: wypełnione osie złączone znakiem × plus jednostka. */
function formatSingle(dimension: SelectionDimension): string {
  // Zero znaczy „oś niewypełniona" — tak samo jak `array_filter` po stronie backendu,
  // które wycina wartości fałszywe (0, puste), a nie tylko null.
  const filled = axesForType(dimension).filter(value => !!value);

  if (filled.length === 0) {
    return '';
  }

  return `${filled.join(SEPARATOR)} ${dimension.unit}`;
}

/**
 * Wartość wymiarów wybranej edycji BEZ etykiet — odpowiednik serwerowego snippetu
 * `inventoryDimensionsWithoutLabels`: wypełnione wymiary złączone `<br>`. Etykietę
 * („Wymiary:") pisze artysta w treści, snippet podstawia samą wartość.
 */
export function formatSelectionDimensions(dimensions: SelectionDimension[] | null | undefined): string {
  if (!dimensions?.length) {
    return '';
  }

  return dimensions
    .map(formatSingle)
    .filter(value => value !== '')
    .join('<br>');
}

/**
 * Reaktywne źródło wartości snippetu „Wymiary" dla wybranej edycji.
 *
 * Konfigurator ceny publikuje tu wymiary aktualnego wyboru (edycja, z fallbackiem na pracę),
 * a snippet `<<frontSnippet(edition_dimensions)>>` je pokazuje. Wzorzec jest ten sam co
 * `ListDetailsService.objectCountOnList` dla licznika pozycji — statyczny strumień, bo snippet
 * i konfigurator to osobne gałęzie drzewa komponentów bez wspólnego wejścia.
 */
@Injectable({providedIn: 'root'})
export class EditionDimensionsService {
  /** Wartość snippetu (może zawierać `<br>`); pusty string = brak wyboru albo brak wymiarów. */
  static readonly value = new BehaviorSubject<string>('');
  static readonly behaviorSubjectChanged = new Subject<void>();

  static update(value: string): void {
    this.value.next(value ?? '');
    this.behaviorSubjectChanged.next();
  }

  static reset(): void {
    this.update('');
  }
}
